import { Link } from 'react-router-dom'
import { Inbox, Search } from 'lucide-react'
import type { SupportTicket } from '@/api/supportApi'
import { SupportStatusPill } from '@/components/support/SupportStatusPill'
import { CHIPS, TYPE_LABEL, timeAgo, type InboxChip } from '@/lib/support/supportView'
import { cn } from '@/lib/utils'

/** Left pane: search, chips and the ticket list (newest first, as Smart Support sends it). */
export function SupportInbox({
  tickets,
  selected,
  chip,
  onChip,
  search,
  onSearch,
  waitingCount,
  now,
  loading,
}: {
  tickets: SupportTicket[]
  selected: string | undefined
  chip: InboxChip
  onChip: (c: InboxChip) => void
  search: string
  onSearch: (q: string) => void
  waitingCount: number
  now: number
  loading: boolean
}) {
  return (
    <aside className='flex min-h-0 flex-col border-r border-border'>
      <div className='flex flex-col gap-2.5 border-b border-border p-3'>
        <label className='flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 text-sm'>
          <Search className='h-4 w-4 text-muted-foreground' aria-hidden />
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder='Search tickets, TKT number, subject…'
            className='w-full bg-transparent outline-none placeholder:text-muted-foreground'
            aria-label='Search tickets'
          />
        </label>
        <div className='flex flex-wrap gap-1.5' role='tablist' aria-label='Filter tickets'>
          {CHIPS.map((c) => (
            <button
              key={c.value}
              type='button'
              role='tab'
              aria-selected={chip === c.value}
              onClick={() => onChip(c.value)}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
                chip === c.value ? 'border-primary/40 bg-primary/10 font-semibold text-primary' : 'border-border text-muted-foreground hover:bg-muted',
              )}
            >
              {c.label}
              {c.value === 'waiting' && waitingCount > 0 ? <span className='ml-1 rounded-full bg-amber-500 px-1.5 text-[10px] text-white'>{waitingCount}</span> : null}
            </button>
          ))}
        </div>
      </div>
      <div className='min-h-0 flex-1 overflow-y-auto'>
        {loading ? (
          <p className='p-4 text-sm text-muted-foreground'>Loading tickets…</p>
        ) : tickets.length === 0 ? (
          <div className='flex flex-col items-center gap-2 px-6 py-12 text-center text-sm text-muted-foreground'>
            <Inbox className='h-6 w-6' aria-hidden />
            No tickets here.
          </div>
        ) : (
          <ul>
            {tickets.map((t) => (
              <li key={t.id}>
                <Link
                  to={`/support/${t.number}`}
                  className={cn(
                    'block border-b border-border px-4 py-3 transition-colors hover:bg-muted/50',
                    selected === String(t.number) && 'bg-primary/5 shadow-[inset_3px_0_0_hsl(var(--primary))]',
                  )}
                >
                  <div className='font-mono text-[11px] text-muted-foreground'>
                    {t.key} · {TYPE_LABEL[t.type] ?? t.type} · {t.priority}
                  </div>
                  <div className='mt-0.5 line-clamp-2 text-sm font-semibold text-foreground'>{t.subject}</div>
                  <div className='mt-1.5 flex items-center gap-2'>
                    <SupportStatusPill status={t.status} />
                    <span className='text-[11px] text-muted-foreground'>
                      {t.raisedByMe ? 'You · ' : t.reporter ? `${t.reporter.name} · ` : ''}
                      {timeAgo(t.updatedAt, now)}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  )
}
